import { useEffect, useState, useCallback, Fragment } from "react";
import { api, money } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Loader2, Truck, ExternalLink, FileDown, Tag, Package } from "lucide-react";
import { toast } from "sonner";

const statusStyle = {
  paid: "bg-emerald-50 text-emerald-600",
  pending: "bg-amber-50 text-amber-600",
  failed: "bg-rose-50 text-rose-600",
  expired: "bg-zinc-100 text-zinc-500",
};

// Split a Stripe address line ("Apollolaan 171") into street + house number.
const parseLine = (line1 = "") => {
  const m = line1.match(/^(.*?)[\s,]+(\d+\s?\w*)$/);
  return m ? { street: m[1].trim(), number: m[2].trim() } : { street: line1, number: "" };
};

function SendyPanel({ order, preferences, upsEnabled, onDone }) {
  const s = order.shipping || {};
  const parsed = parseLine(s.line1);
  const defaultPref = preferences.find((p) => p.default) || preferences[0];
  const [form, setForm] = useState({
    preference_id: order.sendy?.preference_id || defaultPref?.uuid || "",
    company_name: s.name || order.customer_email || "",
    contact: s.name || "",
    email: s.email || order.customer_email || "",
    phone: s.phone || "",
    street: parsed.street,
    number: parsed.number,
    addition: "",
    postal_code: s.postal_code || "",
    city: s.city || "",
    state: s.state || "",
    country: (s.country || "NL").toUpperCase(),
    weight: "1",
    service_code: "11",
  });
  const [busy, setBusy] = useState(false);
  const sendy = order.sendy;
  const ups = order.ups;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const input = "w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-black/10";

  const submit = async (generate) => {
    if (!form.preference_id) return toast.error("Pick a shipping method first.");
    if (!form.street || !form.postal_code || !form.city) return toast.error("Street, postal code and city are required.");
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/sendy/shipment`, { ...form, generate });
      toast.success(generate ? "Shipment created & label generated in Sendy." : "Shipment sent to Sendy.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sendy request failed.");
    } finally { setBusy(false); }
  };

  const genLabel = async () => {
    setBusy(true);
    try { await api.post(`/orders/${order.id}/sendy/label`); toast.success("Label generated."); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate label."); }
    finally { setBusy(false); }
  };

  const downloadLabel = async () => {
    try {
      const r = await api.get(`/orders/${order.id}/sendy/label.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Label not ready yet."); }
  };

  const submitUps = async () => {
    if (!form.street || !form.postal_code || !form.city) return toast.error("Street, postal code and city are required.");
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/ups/shipment`, {
        name: form.company_name, phone: form.phone,
        street: form.street, number: form.number,
        postal_code: form.postal_code, city: form.city, state: form.state,
        country: form.country, weight: parseFloat(form.weight) || 1, weight_unit: "KGS",
        service_code: form.service_code,
      });
      toast.success("Shipment created in UPS — label ready.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "UPS request failed.");
    } finally { setBusy(false); }
  };

  const downloadUps = async () => {
    try {
      const r = await api.get(`/orders/${order.id}/ups/label.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "UPS label not available."); }
  };

  return (
    <div className="bg-zinc-50/70 border-t border-zinc-100 p-5" data-testid={`sendy-panel-${order.id}`}>
      {!order.shipping && (
        <p className="text-xs text-amber-600 font-body mb-3">No delivery address was captured at checkout for this order — enter it manually below.</p>
      )}
      <div className="grid md:grid-cols-3 gap-3 max-w-4xl">
        <div className="md:col-span-3">
          <label className="text-xs font-heading font-bold uppercase tracking-wider text-zinc-500">Shipping method</label>
          <select value={form.preference_id} onChange={set("preference_id")} className={input} data-testid={`sendy-pref-${order.id}`}>
            <option value="">Select…</option>
            {preferences.map((p) => (
              <option key={p.uuid} value={p.uuid}>{p.name} — {p.carrier} / {p.service} ({p.country})</option>
            ))}
          </select>
        </div>
        <input className={input} placeholder="Name / company" value={form.company_name} onChange={set("company_name")} data-testid={`sendy-name-${order.id}`} />
        <input className={input} placeholder="Email" value={form.email} onChange={set("email")} />
        <input className={input} placeholder="Phone" value={form.phone} onChange={set("phone")} />
        <input className={input} placeholder="Street" value={form.street} onChange={set("street")} data-testid={`sendy-street-${order.id}`} />
        <input className={input} placeholder="No." value={form.number} onChange={set("number")} data-testid={`sendy-number-${order.id}`} />
        <input className={input} placeholder="Addition" value={form.addition} onChange={set("addition")} />
        <input className={input} placeholder="Postal code" value={form.postal_code} onChange={set("postal_code")} data-testid={`sendy-postal-${order.id}`} />
        <input className={input} placeholder="City" value={form.city} onChange={set("city")} data-testid={`sendy-city-${order.id}`} />
        <input className={input} placeholder="Country (NL)" value={form.country} onChange={set("country")} />
        {upsEnabled && (
          <>
            <input className={input} placeholder="State/Province (US/CA/IE)" value={form.state} onChange={set("state")} data-testid={`ups-state-${order.id}`} />
            <input className={input} type="number" step="0.1" min="0.1" placeholder="Weight (kg)" value={form.weight} onChange={set("weight")} data-testid={`ups-weight-${order.id}`} />
            <select value={form.service_code} onChange={set("service_code")} className={input} data-testid={`ups-service-${order.id}`}>
              <option value="11">UPS Standard</option>
              <option value="65">UPS Worldwide Saver</option>
              <option value="07">UPS Worldwide Express</option>
              <option value="08">UPS Worldwide Expedited</option>
              <option value="03">UPS Ground (US)</option>
            </select>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        {!sendy?.uuid && (
          <>
            <button onClick={() => submit(false)} disabled={busy} className="casper-btn inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-body font-semibold disabled:opacity-60" data-testid={`sendy-send-${order.id}`}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Truck size={15} />} Send to Sendy
            </button>
            <button onClick={() => submit(true)} disabled={busy} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-body font-semibold border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-60" data-testid={`sendy-send-label-${order.id}`}>
              <Tag size={15} /> Send + create label
            </button>
          </>
        )}
        {upsEnabled && !ups?.tracking && (
          <button onClick={submitUps} disabled={busy} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-body font-semibold text-white bg-[#351c15] hover:brightness-110 disabled:opacity-60" data-testid={`ups-send-${order.id}`}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Package size={15} />} Send To UPS
          </button>
        )}
        {sendy?.uuid && (
          <div className="flex flex-wrap items-center gap-2 text-sm font-body" data-testid={`sendy-status-${order.id}`}>
            <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 font-semibold capitalize">Sendy: {sendy.status}</span>
            <span className="text-zinc-500">{sendy.carrier} · {sendy.service}</span>
            {sendy.package_number && <span className="text-zinc-500">#{sendy.package_number}</span>}
            {sendy.estimated_price != null && <span className="text-zinc-500">~€{Number(sendy.estimated_price).toFixed(2)}</span>}
            {sendy.url && <a href={sendy.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline"><ExternalLink size={13} /> Open in Sendy</a>}
            {!sendy.label_generated && (
              <button onClick={genLabel} disabled={busy} className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-60" data-testid={`sendy-genlabel-${order.id}`}>
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Tag size={13} />} Generate label
              </button>
            )}
            <button onClick={downloadLabel} className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-zinc-300 bg-white hover:bg-zinc-50" data-testid={`sendy-download-${order.id}`}>
              <FileDown size={13} /> Download label
            </button>
          </div>
        )}
        {ups?.tracking && (
          <div className="flex flex-wrap items-center gap-2 text-sm font-body" data-testid={`ups-status-${order.id}`}>
            <span className="px-2.5 py-1 rounded-full bg-[#351c15]/10 text-[#351c15] font-semibold">UPS · {ups.tracking}</span>
            {ups.amount != null && <span className="text-zinc-500">~{ups.currency || ""} {Number(ups.amount).toFixed(2)}</span>}
            {ups.environment === "cie" && <span className="text-amber-600 text-xs">test</span>}
            <button onClick={downloadUps} className="inline-flex items-center gap-1 rounded-full px-3 py-1 border border-zinc-300 bg-white hover:bg-zinc-50" data-testid={`ups-download-${order.id}`}>
              <FileDown size={13} /> Download UPS label
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function OrdersPage() {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [preferences, setPreferences] = useState([]);
  const [upsEnabled, setUpsEnabled] = useState(false);
  const { selectedStore, currentStore } = useStore();

  const load = useCallback(() => { api.get("/orders").then((r) => setOrders(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/sendy/preferences").then((r) => setPreferences(r.data)).catch(() => setPreferences([]));
    api.get("/admin/ups-settings").then((r) => setUpsEnabled(!!r.data?.enabled && !!r.data?.secret_set)).catch(() => {});
  }, []);

  if (!orders) return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-zinc-400" /></div>;

  const scoped = selectedStore === "all" || !selectedStore ? orders : orders.filter((o) => o.store_id === selectedStore);
  const filtered = filter === "all" ? scoped : scoped.filter((o) => o.status === filter);
  const scopeLabel = selectedStore === "all" || !selectedStore ? "All stores" : (currentStore?.name || "Store");

  const refreshExpanded = () => load();

  return (
    <div className="animate-fade-up" data-testid="orders-page">
      <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Sales · {scopeLabel}</span>
      <h1 className="font-heading font-light text-4xl tracking-tight mt-1 mb-6">Orders</h1>

      <div className="flex gap-2 mb-6">
        {["all", "paid", "pending", "failed"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-4 py-2 rounded-full text-sm font-body font-semibold capitalize transition-colors ${filter === f ? "bg-black text-white" : "bg-white border border-zinc-200 text-zinc-500 hover:bg-zinc-50"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200/80 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-zinc-50 text-zinc-400 text-xs uppercase tracking-wider font-heading">
            <tr>
              <th className="text-left px-5 py-3 font-bold">Order</th>
              <th className="text-left px-5 py-3 font-bold">Store</th>
              <th className="text-left px-5 py-3 font-bold">Customer</th>
              <th className="text-left px-5 py-3 font-bold">Date</th>
              <th className="text-right px-5 py-3 font-bold">Amount</th>
              <th className="text-right px-5 py-3 font-bold">Status</th>
              <th className="text-right px-5 py-3 font-bold">Fulfilment</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <Fragment key={o.id}>
                <tr className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`order-${o.id}`}>
                  <td className="px-5 py-4 font-body">{o.items.map((i) => `${i.title} ×${i.quantity}`).join(", ")}</td>
                  <td className="px-5 py-4 text-zinc-500">@{o.store_handle}</td>
                  <td className="px-5 py-4 text-zinc-500">{o.customer_email || "—"}</td>
                  <td className="px-5 py-4 text-zinc-500">{o.created_at.slice(0, 10)}</td>
                  <td className="px-5 py-4 text-right font-heading font-semibold">{money(o.amount)}</td>
                  <td className="px-5 py-4 text-right">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusStyle[o.status] || "bg-zinc-100"}`}>{o.status}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    {o.status === "paid" ? (
                      <button onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-body font-semibold border border-zinc-200 hover:bg-zinc-50"
                        data-testid={`ship-toggle-${o.id}`}>
                        <Truck size={13} /> {o.sendy?.uuid ? "Shipped" : "Ship"}
                      </button>
                    ) : <span className="text-zinc-300 text-xs">—</span>}
                  </td>
                </tr>
                {expanded === o.id && (
                  <tr>
                    <td colSpan={7} className="p-0">
                      <SendyPanel order={o} preferences={preferences} upsEnabled={upsEnabled} onDone={refreshExpanded} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>
        {filtered.length === 0 && <p className="text-center text-zinc-400 py-16 font-body">No orders.</p>}
      </div>
    </div>
  );
}
